import { QueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { ApiError } from './client';

/**
 * Query client configuration with global defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 30 seconds
      staleTime: 30 * 1000,

      // Cache time: 5 minutes
      gcTime: 5 * 60 * 1000,

      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus (enabled)
      refetchOnWindowFocus: true,

      // Refetch on reconnect (enabled)
      refetchOnReconnect: true,
    },

    mutations: {
      // Global error handler for mutations
      onError: (error) => {
        const message =
          error instanceof ApiError
            ? error.message
            : 'An unexpected error occurred';

        notifications.show({
          title: 'Error',
          message,
          color: 'red',
        });
      },
    },
  },
});

/**
 * Query key factory for type-safe query keys
 */
export const queryKeys = {
  devices: {
    all: ['devices'] as const,
    list: (filters: Record<string, unknown>) => ['devices', 'list', filters] as const,
    detail: (id: string) => ['devices', 'detail', id] as const,
  },
  sites: {
    all: ['sites'] as const,
    list: () => ['sites', 'list'] as const,
  },
  rooms: {
    all: ['rooms'] as const,
    list: (siteId?: string) => ['rooms', 'list', { siteId }] as const,
    detail: (id: string) => ['rooms', 'detail', id] as const,
    types: () => ['rooms', 'types'] as const,
    devices: (roomId: string) => ['rooms', 'devices', roomId] as const,
  },
  assets: {
    all: ['assets'] as const,
    list: (filters?: Record<string, unknown>) => ['assets', 'list', filters] as const,
    detail: (id: string) => ['assets', 'detail', id] as const,
    types: () => ['assets', 'types'] as const,
    statuses: () => ['assets', 'statuses'] as const,
    summary: () => ['assets', 'summary'] as const,
    availableDevices: () => ['assets', 'availableDevices'] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    list: (filters: Record<string, unknown>) => ['tasks', 'list', filters] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
  },
  preview: {
    all: ['preview'] as const,
    detail: (id: string) => ['preview', 'detail', id] as const,
  },
  fog: {
    health: ['fog', 'health'] as const,
    dashboard: ['fog', 'dashboard'] as const,
    systemInfo: ['fog', 'systemInfo'] as const,
  },
  remote: {
    all: ['remote'] as const,
    statusSummary: ['remote', 'statusSummary'] as const,
    statusList: ['remote', 'statusList'] as const,
    status: (deviceId: string) => ['remote', 'status', deviceId] as const,
    powerLogs: ['remote', 'powerLogs'] as const,
    devicePowerLogs: (deviceId: string) => ['remote', 'powerLogs', deviceId] as const,
    credentials: (deviceId: string) => ['remote', 'credentials', deviceId] as const,
  },
  scans: {
    all: ['scans'] as const,
    scripts: ['scans', 'scripts'] as const,
    script: (id: string) => ['scans', 'scripts', id] as const,
    jobs: ['scans', 'jobs'] as const,
    jobsRunning: ['scans', 'jobs', 'running'] as const,
    job: (id: string) => ['scans', 'jobs', id] as const,
    jobResults: (id: string) => ['scans', 'jobs', id, 'results'] as const,
  },
};
